import { useCallback, useState } from 'react';
import type { CallTranscriptSegmentView, CoachingHint } from '@brimo/shared';
import { useRealtime, type RealtimeEvent } from '../../lib/events';

/**
 * The live transcript and the live hints of one call (docs/dev/api-dialer.md „Live-Transkript"): collected from the
 * realtime events `call.transcript` and `call.hint` the server pushes to the caller. Everything resets when the call
 * changes; segments are kept in Twilio's order (a retried event is ignored).
 */

export interface LiveTranscript {
  segments: CallTranscriptSegmentView[];
  hints: CoachingHint[];
}

const EMPTY: LiveTranscript = { segments: [], hints: [] };

function isSegment(value: unknown): value is CallTranscriptSegmentView {
  if (typeof value !== 'object' || value === null) return false;
  const segment = value as Record<string, unknown>;
  return (
    typeof segment['seq'] === 'number' &&
    (segment['speaker'] === 'setter' || segment['speaker'] === 'contact') &&
    typeof segment['text'] === 'string'
  );
}

function isHint(value: unknown): value is CoachingHint {
  if (typeof value !== 'object' || value === null) return false;
  const hint = value as Record<string, unknown>;
  return (
    (hint['kind'] === 'objection' || hint['kind'] === 'checkpoint' || hint['kind'] === 'booking_intent') &&
    typeof hint['key'] === 'string' &&
    typeof hint['title'] === 'string' &&
    typeof hint['text'] === 'string'
  );
}

export function useLiveTranscript(callId: string | null): LiveTranscript {
  const [state, setState] = useState<{ callId: string | null; data: LiveTranscript }>({
    callId,
    data: EMPTY,
  });
  const current = state.callId === callId ? state.data : EMPTY;
  if (state.callId !== callId) setState({ callId, data: EMPTY });

  const onSegment = useCallback(
    (event: RealtimeEvent) => {
      const segment = event.payload['segment'];
      if (callId === null || event.payload['callId'] !== callId || !isSegment(segment)) return;
      setState((previous) => {
        const data = previous.callId === callId ? previous.data : EMPTY;
        if (data.segments.some((existing) => existing.seq === segment.seq)) return previous;
        const segments = [...data.segments, segment].sort((a, b) => a.seq - b.seq);
        return { callId, data: { ...data, segments } };
      });
    },
    [callId],
  );

  const onHint = useCallback(
    (event: RealtimeEvent) => {
      const hint = event.payload['hint'];
      if (callId === null || event.payload['callId'] !== callId || !isHint(hint)) return;
      setState((previous) => {
        const data = previous.callId === callId ? previous.data : EMPTY;
        if (data.hints.some((existing) => existing.key === hint.key)) return previous;
        return { callId, data: { ...data, hints: [...data.hints, hint] } };
      });
    },
    [callId],
  );

  useRealtime('call.transcript', onSegment);
  useRealtime('call.hint', onHint);
  return current;
}
